/**
 * Authentication context and provider for Cognito-based auth.
 *
 * Provides:
 * - Login/logout flow with Cognito Hosted UI
 * - Token management (storage, refresh)
 * - User information from backend
 * - Protected route functionality
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AuthContext } from './context';
import type {
  AuthConfig,
  AuthContextValue,
  AuthState,
  AuthUser,
} from './types';

const AUTH_CONFIG_ENDPOINT = '/api/auth/config';
const AUTH_STATUS_ENDPOINT = '/api/auth/status';

// Storage keys (refresh token is in an HttpOnly cookie, not localStorage)
const TOKEN_KEY = 'auth_id_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
};

/**
 * Parse URL query params for OAuth authorization code flow.
 */
function parseQueryParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(initialState);
  const [config, setConfig] = useState<AuthConfig | null>(null);

  // Get stored token
  const getStoredToken = useCallback((): string | null => {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !expiry) {
      return null;
    }

    // Check if token is expired
    const expiryTime = parseInt(expiry, 10);
    if (Date.now() > expiryTime) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      return null;
    }

    return token;
  }, []);

  // Store token
  const storeToken = useCallback((token: string, expiresIn: number) => {
    localStorage.setItem(TOKEN_KEY, token);
    // Store expiry time (current time + expires_in seconds - 60 seconds buffer)
    const expiryTime = Date.now() + (expiresIn - 60) * 1000;
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
  }, []);

  // Clear ID token from localStorage
  const clearToken = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }, []);

  // Silently refresh the ID token using the HttpOnly refresh token cookie
  const silentRefresh = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      storeToken(data.id_token, data.expires_in);
      return true;
    } catch {
      return false;
    }
  }, [storeToken]);

  // Fetch auth config from backend
  const fetchConfig = useCallback(async (): Promise<AuthConfig | null> => {
    try {
      const response = await fetch(AUTH_CONFIG_ENDPOINT);
      if (!response.ok) {
        console.error('Failed to fetch auth config');
        return null;
      }
      const data = await response.json();
      return {
        enabled: data.enabled,
        cognitoDomain: data.cognito_domain,
        clientId: data.client_id,
        redirectUri:
          data.redirect_uri || `${window.location.origin}/auth/callback`,
        logoutUri: data.logout_uri || window.location.origin,
      };
    } catch (error) {
      console.error('Error fetching auth config:', error);
      return null;
    }
  }, []);

  // Check auth status with backend
  const checkAuthStatus = useCallback(
    async (token: string): Promise<AuthUser | null> => {
      try {
        const response = await fetch(AUTH_STATUS_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          return null;
        }

        const data = await response.json();
        if (!data.authenticated || !data.user) {
          return null;
        }

        return {
          sub: data.user.sub,
          email: data.user.email,
          emailVerified: data.user.email_verified,
          name: data.user.name,
          groups: data.user.groups,
          isAdmin: data.user.is_admin,
        };
      } catch (error) {
        console.error('Error checking auth status:', error);
        return null;
      }
    },
    []
  );

  // Exchange authorization code for tokens (via backend to avoid CORS)
  const exchangeCodeForTokens = useCallback(
    async (code: string, authConfig: AuthConfig): Promise<string | null> => {
      if (!authConfig.clientId) {
        return null;
      }

      const redirectUri =
        authConfig.redirectUri || `${window.location.origin}/auth/callback`;

      try {
        // Use backend endpoint to proxy token exchange (avoids CORS issues)
        // Refresh token is set as an HttpOnly cookie by the backend
        const response = await fetch('/api/auth/token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            redirect_uri: redirectUri,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Token exchange failed:', errorData);
          return null;
        }

        const data = await response.json();
        storeToken(data.id_token, data.expires_in);
        return data.id_token;
      } catch (error) {
        console.error('Error exchanging code for tokens:', error);
        return null;
      }
    },
    [storeToken]
  );

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      // Fetch auth config
      const authConfig = await fetchConfig();
      setConfig(authConfig);

      // If auth is disabled, mark as authenticated with mock user for dev
      if (!authConfig?.enabled) {
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: {
            sub: 'dev-user',
            email: 'dev@localhost',
            emailVerified: true,
            name: 'Development User',
            groups: ['admins'],
            isAdmin: true,
          },
          error: null,
        });
        return;
      }

      // Check for authorization code in URL (OAuth2 callback)
      const queryParams = parseQueryParams();
      const code = queryParams.get('code');

      if (code) {
        // Exchange code for tokens (also sets refresh token cookie)
        const token = await exchangeCodeForTokens(code, authConfig);

        // Clean up URL
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        if (token) {
          const user = await checkAuthStatus(token);
          if (user) {
            setState({
              isAuthenticated: true,
              isLoading: false,
              user,
              error: null,
            });
            return;
          }
        }
      }

      // Check for existing token
      const token = getStoredToken();
      if (token) {
        const user = await checkAuthStatus(token);
        if (user) {
          setState({
            isAuthenticated: true,
            isLoading: false,
            user,
            error: null,
          });
          return;
        }
        clearToken();
      }

      // ID token expired or missing -- try silent refresh via HttpOnly cookie
      const refreshed = await silentRefresh();
      if (refreshed) {
        const newToken = getStoredToken();
        if (newToken) {
          const user = await checkAuthStatus(newToken);
          if (user) {
            setState({
              isAuthenticated: true,
              isLoading: false,
              user,
              error: null,
            });
            return;
          }
        }
        clearToken();
      }

      // Not authenticated
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    };

    initAuth();
  }, [
    fetchConfig,
    checkAuthStatus,
    getStoredToken,
    clearToken,
    silentRefresh,
    exchangeCodeForTokens,
  ]);

  // Proactive token refresh timer -- refreshes ~5 minutes before expiry
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!state.isAuthenticated || !config?.enabled) {
      return;
    }

    const scheduleRefresh = () => {
      const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
      if (!expiryStr) return;

      const expiryTime = parseInt(expiryStr, 10);
      const refreshAt = expiryTime - 5 * 60 * 1000;
      const delay = refreshAt - Date.now();

      if (delay <= 0) {
        silentRefresh().then((success) => {
          if (success) {
            scheduleRefresh();
          } else {
            setState((prev) => ({
              ...prev,
              isAuthenticated: false,
              user: null,
            }));
          }
        });
        return;
      }

      refreshTimerRef.current = setTimeout(async () => {
        const success = await silentRefresh();
        if (success) {
          scheduleRefresh();
        } else {
          setState((prev) => ({
            ...prev,
            isAuthenticated: false,
            user: null,
          }));
        }
      }, delay);
    };

    scheduleRefresh();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [state.isAuthenticated, config?.enabled, silentRefresh]);

  // Login - redirect to Cognito hosted UI
  const login = useCallback(() => {
    if (!config?.enabled || !config.cognitoDomain || !config.clientId) {
      console.error('Auth not configured');
      return;
    }

    const redirectUri =
      config.redirectUri || `${window.location.origin}/auth/callback`;
    const loginUrl = new URL(`${config.cognitoDomain}/login`);
    loginUrl.searchParams.set('client_id', config.clientId);
    loginUrl.searchParams.set('response_type', 'code');
    loginUrl.searchParams.set('scope', 'email openid profile');
    loginUrl.searchParams.set('redirect_uri', redirectUri);

    window.location.href = loginUrl.toString();
  }, [config]);

  // Logout - revoke refresh token cookie, clear local state, redirect
  const logout = useCallback(() => {
    fetch('/api/auth/revoke', { method: 'POST' }).catch(() => {});

    clearToken();

    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
    });

    if (config?.enabled && config.cognitoDomain && config.clientId) {
      const logoutUri = config.logoutUri || window.location.origin;
      const logoutUrl = new URL(`${config.cognitoDomain}/logout`);
      logoutUrl.searchParams.set('client_id', config.clientId);
      logoutUrl.searchParams.set('logout_uri', logoutUri);
      window.location.href = logoutUrl.toString();
    }
  }, [config, clearToken]);

  // Get access token for API calls
  const getAccessToken = useCallback((): string | null => {
    return getStoredToken();
  }, [getStoredToken]);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
